import classNames from 'classnames'
import { useTranslation } from 'next-i18next'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useSwipeableProductCardExperiment } from '@src/experiments/statsig-integration/use-swipeable-product-card-experiment.hook'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts/NoProduct'
import { SkeletonPlaceholders } from '@src/quince-components/src/atoms/skeletonPlaceholders/SkeletonPlaceholders'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import { getTrackingMetaData, PRODUCT_CARD_OPTION_LIMIT, PRODUCT_VIEW_TRACKER } from './searchResults.constants'
import styles from './searchResults.module.scss'

export interface SearchResultsContentLegacyProps {
  productCards: IProductCard[]
  showLoader: boolean
  maxWidth: number
  productViewTracker?: string
  searchQuery: string
  onResetFilters: () => void
  selectedFilters: SelectedFilter
  showDesktopFilterWithSidebar: boolean
}

export const SearchResultsContentLegacy = ({
  productCards,
  showLoader,
  maxWidth,
  searchQuery,
  onResetFilters,
  selectedFilters,
  showDesktopFilterWithSidebar,
}: SearchResultsContentLegacyProps) => {
  const { t } = useTranslation()
  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const enableImageCarousel = useSwipeableProductCardExperiment()

  if (showLoader) {
    return (
      <div className={styles.searchResultsWrapper}>
        <SkeletonPlaceholders length={10} />
      </div>
    )
  }

  if (productCards.length > 0) {
    return (
      <div
        className={classNames(`${styles.searchResultsWrapper} ${styles.searchResultsWrapper__newCLPFilters ?? ''}`, {
          [styles.searchResultsWrapper__newCLPFiltersDesktopGroupB ?? '']: showDesktopFilterWithSidebar,
        })}
      >
        {productCards.map((productCard, pIdx) => {
          const { product } = productCard
          const initialSelectedVariant = getInitialSelectedVariant({
            cardVariants: product.cardVariants,
          })
          return (
            <ProductCard
              shouldShowMore
              optionLimit={PRODUCT_CARD_OPTION_LIMIT}
              maxWidth={maxWidth}
              key={`${product.productId}_${initialSelectedVariant?.displayConfig.value}_${pIdx}`}
              className={styles.productCard}
              productCard={productCard}
              tracker={PRODUCT_VIEW_TRACKER}
              productViewTracker={PRODUCT_VIEW_TRACKER}
              searchQuery={searchQuery}
              position={pIdx + 1}
              enableImageCarousel={enableImageCarousel}
              productClickTrackConfig={{
                trackInternal: true,
                category: 'search_results_collection',
                action: 'search_by_query',
                context: {
                  searchQuery: searchQuery,
                  position: pIdx,
                  product_position: pIdx,
                  ...getTrackingMetaData().context,
                },
              }}
              onClickCb={() => {
                setFindingMethod[FINDING_METHOD_AGGREGATE.SEARCH]?.()
              }}
              isPageEligibleForHolidayTag={true}
            />
          )
        })}
      </div>
    )
  }

  return (
    <>
      {!hasAppliedFilters && (
        <h1
          className={`${styles.searchResultsHeader__title} ${styles.searchResultsHeader__title__noResults}`}
        >{`"${searchQuery.toUpperCase()}"`}</h1>
      )}
      <div className={styles.noSearchResultsWrapper} data-testid={TEST_LOCATORS.NO_SEARCH_RESULTS}>
        {hasAppliedFilters ? (
          <NoProduct onResetFilters={onResetFilters} isFiltersApplied={hasAppliedFilters} />
        ) : (
          t('no_search_results')
        )}
      </div>
    </>
  )
}
